import api from '@/lib/api';

/* ── Vocabulary ─────────────────────────────────────────── */

export type PartnerStatus = 'draft' | 'pending' | 'active' | 'suspended' | 'terminated';
export type PartnerType = 'distributor' | 'franchise' | 'lco' | 'reseller';
export type NasStatus = 'online' | 'offline' | 'disabled';

export const STATUS_LABEL: Record<PartnerStatus, string> = {
  draft: 'Draft',
  pending: 'Pending Approval',
  active: 'Active',
  suspended: 'Suspended',
  terminated: 'Terminated',
};

/** prepaid: renewals are charged to the partner's wallet, net of commission. postpaid: paid in the settlement. */
export type BillingMode = 'prepaid' | 'postpaid';
export const BILLING_MODE_LABEL: Record<BillingMode, string> = { prepaid: 'Prepaid wallet', postpaid: 'Postpaid settlement' };
export const BILLING_MODE_HINT: Record<BillingMode, string> = {
  prepaid: 'Each renewal is charged to the partner’s wallet, less its commission. A renewal the wallet can’t cover is refused.',
  postpaid: 'Nothing is charged to the wallet. Commission goes to commission payable and is paid in the settlement.',
};

export const TYPE_LABEL: Record<PartnerType, string> = {
  distributor: 'Distributor',
  franchise: 'Franchise',
  lco: 'LCO',
  reseller: 'Reseller',
};

export const WALLET_MODES = [
  { value: 'bank_transfer', label: 'Bank transfer (NEFT/IMPS)' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash at office' },
] as const;

export const CAPABILITIES = [
  { key: 'createConnections', label: 'Create new connections', hint: 'Onboard subscribers, including eKYC and CAF' },
  { key: 'renewSubscribers', label: 'Renew subscribers', hint: 'Renew their own subscribers' },
  { key: 'collectCash', label: 'Collect cash from subscribers', hint: 'Collections count toward commission' },
  { key: 'assistCaf', label: 'Assist CAF signing', hint: 'Their name is printed on the signed CAF' },
  { key: 'changePlans', label: 'Change plans', hint: 'Upgrade or downgrade without staff approval' },
  { key: 'issueRefunds', label: 'Issue refunds', hint: 'Reverses a wallet debit' },
] as const;
export type CapabilityKey = (typeof CAPABILITIES)[number]['key'];
export type Capabilities = Record<CapabilityKey, boolean>;

export const NOTIFICATIONS = [
  { key: 'lowBalance', label: 'Low wallet balance', hint: 'When the wallet drops below the alert level' },
  { key: 'monthlyStatement', label: 'Monthly commission statement', hint: 'On the 1st of each month' },
  { key: 'expiringDigest', label: 'Subscribers expiring this week', hint: 'Digest every Monday' },
  { key: 'networkOutages', label: 'Network outages in their areas', hint: 'As they happen' },
] as const;
export type NotificationKey = (typeof NOTIFICATIONS)[number]['key'];
export type NotificationPrefs = Record<NotificationKey, boolean>;

/* ── Shapes ─────────────────────────────────────────────── */

export interface PartnerListItem {
  id: string;
  name: string;
  code: string;
  type: PartnerType | null;
  region: string | null;
  status: PartnerStatus;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  gstNumber: string | null;
  walletBalance: number;
  creditLimit: number;
  lowBalanceThreshold: number;
  walletLow: boolean;
  subscriberCount: number;
  activeSubscribers: number;
  lastLoginAt: string | null;
  lastLoginName: string | null;
  parent: { id: string; name: string; code: string } | null;
  submittedAt: string | null;
  createdAt: string;
}

export interface PartnerSummary {
  total: number;
  active: number;
  pending: number;
  draft: number;
  suspended: number;
  terminated: number;
  regions: number;
  walletTotal: number;
  lowBalance: number;
  negativeBalance: number;
  oldestPendingAt: string | null;
  subscribers: number;
  activeSubscribers: number;
  pendingApprovals: number;
}

export type ApprovalKind = 'termination' | 'credit_limit' | 'commission_scheme' | 'commission_defaults';

export interface PendingApproval {
  id: string;
  kind: ApprovalKind;
  payload: {
    from?: number; to?: number; subscriberCount?: number; statusAtRequest?: string;
    /** Commission requests: the version, when it starts, the partner type (for a default) and what rises. */
    version?: number; effectiveFrom?: string; partnerType?: PartnerType;
    increases?: { label: string; from: number; to: number }[];
  };
  reason: string;
  requestedBy: { id: string; name: string | null };
  requestedAt: string;
}

export interface PartnerDetail {
  id: string;
  name: string;
  code: string;
  legalName: string | null;
  type: PartnerType | null;
  region: string | null;
  status: PartnerStatus;
  parent: { id: string; name: string; code: string; type: PartnerType | null } | null;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  gstNumber: string | null;
  panNumber: string | null;
  notes: string | null;
  commercial: {
    revenueSharePercent: number;
    newConnectionCommission: number;
    rechargeCommissionPercent: number;
    collectionCommissionPercent: number;
    creditLimit: number;
    lowBalanceThreshold: number;
    settlementCycle: string;
    autoInvoiceEnabled: boolean;
    billingMode: BillingMode;
  };
  wallet: { balance: number; creditLimit: number; available: number; creditUsed: number; lowBalanceThreshold: number; isLow: boolean };
  commissionPayable: number;
  capabilities: Capabilities;
  notificationPrefs: NotificationPrefs;
  counts: {
    subscribers: number; activeSubscribers: number; expiring7d: number; operators: number;
    areas: number; users: number; routers: number; pools: number;
  };
  lastLogin: { at: string; name: string | null; role: string | null } | null;
  lifecycle: {
    createdAt: string | null; createdBy: string | null;
    submittedAt: string | null; submittedBy: string | null;
    approvedAt: string | null; approvedBy: string | null;
    sentBackAt: string | null; sentBackBy: string | null; sentBackNote: string | null;
    suspendedAt: string | null; suspendedBy: string | null; suspensionReason: string | null; suspensionBlocksRenewals: boolean;
    terminatedAt: string | null; terminatedBy: string | null; terminationReason: string | null;
  };
  pendingApprovals: PendingApproval[];
  /** The commission version this partner earns by today: its own, its type's default, or none. */
  commission: {
    source: 'partner' | 'type' | 'none';
    version: number | null;
    effectiveFrom: string | null;
    dataPct: number | null;
    combinedPct: number | null;
    hasDraft: boolean;
    scheduledFrom: string | null;
  };
  createdAt: string;
}

export interface PartnerOverview {
  subscribers: {
    total: number; active: number; expired: number; suspended: number; expiring7d: number;
    newThisMonth: number; renewalsLast30d: number | null;
  };
  operators: { id: string; name: string; code: string | null; area: string | null; city: string | null; status: string; subscriberCount: number }[];
  areaCount: number;
  recentActivity: { id: string; action: string; at: string; user: string | null; changes: Record<string, unknown> }[];
}

export interface PartnerSubscriberRow {
  id: string;
  subscriberCode: string;
  name: string | null;
  phone: string | null;
  username: string | null;
  status: string;
  expiryDate: string | null;
  plan: { name: string; price: number } | null;
  router: { id: string; name: string } | null;
  area: string | null;
  operator: { id: string; name: string } | null;
  outsideScope: boolean;
}

export interface NasInfo {
  id: string;
  name: string;
  ip: string | null;
  vendor: string | null;
  role: 'NAS' | 'BNG';
  location: string | null;
  pop: { id: string; name: string } | null;
  status: NasStatus;
  lastPolledAt: string | null;
}

export interface PoolInfo {
  id: string;
  nasId: string | null;
  name: string;
  family: 'v4' | 'v6';
  ranges: string;
  totalIps: number;
  usedIps: number;
  freeIps: number;
  isPublic: boolean;
  isActive: boolean;
  delegatedPrefixLength: number | null;
  kind: string;
}

export interface NasAssignment {
  id: string;
  nas: NasInfo;
  realm: string | null;
  effectiveFrom: string;
  reason: string;
  assignedAt: string;
  assignedBy: string | null;
  subscriberCount: number;
  pools: PoolInfo[];
  olts: { id: string; name: string; vendor: string | null; model: string | null; subscriberCount: number }[];
}

export interface PartnerNetwork {
  assignments: NasAssignment[];
  totals: {
    routers: number; online: number; pops: number; pools: number; v4: number; v6: number;
    subscribers: number; onAssignedRouters: number; onUnassignedRouters: number; withoutRouter: number;
  };
  stray: { nasId: string; name: string | null; subscriberCount: number }[];
  openTasks: {
    id: string; assignmentId: string | null; nasId: string | null; nasName: string | null; subscriberCount: number;
    note: string | null; status: string; createdAt: string; createdBy: string | null;
  }[];
}

export interface AvailableRouter extends NasInfo {
  sharedWith: number;
  assignedHere: boolean;
  pools: (PoolInfo & { owner: { id: string; name: string; code: string; isThisPartner: boolean } | null })[];
}

export interface PartnerView {
  routers: {
    id: string; name: string; role: string; pop: { id: string; name: string } | null; status: NasStatus; subscriberCount: number;
    pools: { id: string; name: string; family: 'v4' | 'v6'; totalIps: number; freeIps: number; kind: string }[];
  }[];
  totals: { routers: number; online: number; pools: number; subscribers: number };
}

export interface Pop { id: string; name: string; code: string | null; city: string | null; address: string | null; isActive: boolean; routerCount: number; createdAt: string }
export interface RouterRow extends NasInfo { partnerCount: number; poolCount: number }

export interface WalletSummary {
  balance: number;
  creditLimit: number;
  available: number;
  creditUsed: number;
  lowBalanceThreshold: number;
  isLow: boolean;
  commissionPayable: number;
  partnerStatus: PartnerStatus;
  topupsBlocked: boolean;
  lastTopup: { amount: number; reference: string | null; at: string } | null;
}

export interface WalletTxn {
  id: string;
  at: string;
  type: 'opening' | 'topup' | 'debit' | 'adjustment' | 'reversal' | 'renewal_charge' | 'commission';
  /** Signed: a debit is negative. */
  amount: number;
  balanceAfter: number;
  balanceBefore: number;
  /** PW-202609-0001. Null on entries from before transaction numbers. */
  txnNumber: string | null;
  /** A recharge: what the subscriber was billed (GST included). */
  grossAmount: number | null;
  /** The commission netted off a recharge, or credited when a subscriber paid NissNet. */
  commissionAmount: number | null;
  tdsAmount: number | null;
  renewal: { id: string; planName: string | null; days: number | null; source: string | null } | null;
  reversesTxnNumber: string | null;
  mode: string | null;
  modeLabel: string | null;
  reference: string | null;
  description: string;
  subscriber: { id: string; code: string | null; name: string | null } | null;
  invoiceId: string | null;
  createdBy: string | null;
  actingFor: string | null;
}

export interface Settlement {
  id: string;
  partnerId: string;
  partnerName: string | null;
  period: string;
  totalCommission: number;
  deductions: number;
  netAmount: number;
  status: string;
  paidAt: string | null;
  paymentReference: string | null;
  notes: string | null;
  createdAt: string;
}

export interface CommissionSummary {
  byType: Record<string, { count: number; total: number }>;
  grandTotal: number;
  period: string;
}

export interface PartnerBilling {
  commissionPayable: number;
  terms: PartnerDetail['commercial'];
  settlements: Settlement[];
  currentPeriod: CommissionSummary;
  previousPeriod: CommissionSummary;
  commissions: { status: string; count: number; total: number }[];
}

export interface PartnerComms {
  recipients: { phone: string; name: string | null; label: string }[];
  prefs: NotificationPrefs;
  messages: {
    id: string; channel: 'whatsapp' | 'sms'; to: string; title: string; preview: string | null;
    status: string; failedReason: string | null; direction: string; at: string;
  }[];
}

export interface PartnerUser {
  id: string;
  firstName: string;
  lastName: string | null;
  name: string;
  email: string;
  phone: string | null;
  role: { id: string; name: string; displayName: string } | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PartnerRole {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  permissions: string[];
  isPartnerAdmin: boolean;
  userCount: number;
}

export interface ImpersonationSessionRow {
  id: string;
  reason: string;
  staff: string | null;
  partnerUser: string | null;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  endReason: string | null;
  active: boolean;
}

export interface PartnerIntegrations {
  live: { apiKeyAuth: boolean; webhookDelivery: boolean };
  scopes: { value: string; label: string }[];
  events: string[];
  apiKeys: {
    id: string; name: string; keyPrefix: string; scopes: string[]; isActive: boolean; expiresAt: string | null;
    expired: boolean; lastUsedAt: string | null; usageCount: number; createdAt: string; createdBy: string | null;
  }[];
  webhooks: {
    id: string; name: string; url: string; events: string[]; isActive: boolean; lastTriggeredAt: string | null;
    lastStatus: string | null; failureCount: number; createdAt: string;
  }[];
}

export interface PartnerPlans {
  restricted: boolean;
  plans: { id: string; name: string; price: number; downloadSpeed: string | number | null; validityDays: number | null; active: boolean; allowed: boolean }[];
}

export interface AuditFieldChange { field: string; before: unknown; after: unknown; redacted?: boolean }
export interface AuditEntry {
  id: string;
  at: string;
  action: string;
  actor: 'staff' | 'customer' | 'system';
  userName: string | null;
  userEmail: string | null;
  userRole: string | null;
  actingFor: string | null;
  status: 'success' | 'failed';
  error: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  changes: AuditFieldChange[];
  context: Record<string, unknown> | null;
}

export interface ApprovalRow {
  id: string;
  /** Null for a partner-type commission default, which belongs to no partner. */
  partner: { id: string; name: string; code: string } | null;
  schemeId: string | null;
  kind: ApprovalKind;
  payload: PendingApproval['payload'];
  reason: string;
  status: string;
  requestedBy: { id: string; name: string | null };
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
}

export interface ImportColumn { key: string; header: string; required: boolean; example: string }
export interface ImportPreview {
  fileName: string;
  total: number;
  ready: { rowNumber: number; name: string; raw: Record<string, unknown> }[];
  problems: { row: number; name: string | null; messages: string[] }[];
}

export interface PortalOverview {
  partner: { id: string; name: string; code: string; type: PartnerType | null; region: string | null; status: PartnerStatus };
  capabilities: Capabilities;
  subscribers: { total: number; active: number; expired: number; expiring7d: number };
  wallet: WalletSummary & { recent: WalletTxn[] };
  network: PartnerView['totals'];
  canManageUsers: boolean;
  impersonation: { sessionId: string } | null;
}

export type PartnerInput = Partial<{
  name: string; type: PartnerType | null; region: string | null; parentPartnerId: string | null; legalName: string | null;
  gstNumber: string | null; panNumber: string | null; contactPerson: string | null; phone: string | null; email: string | null;
  address: string | null; city: string | null; state: string | null; revenueSharePercent: number; newConnectionCommission: number;
  rechargeCommissionPercent: number; collectionCommissionPercent: number; creditLimit: number; lowBalanceThreshold: number;
  settlementCycle: string; billingMode: BillingMode; notes: string | null;
}>;

type PendingApprovalResult = { approvalId: string; kind: string; from: number; to: number } | null;

/* ── API ────────────────────────────────────────────────── */

async function data<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  return (await p).data.data;
}

export const partnersApi = {
  list: (params: Record<string, string | number | undefined>) =>
    data<{ data: PartnerListItem[]; total: number; page: number; limit: number }>(api.get('/partners', { params: clean(params) })),
  summary: () => data<PartnerSummary>(api.get('/partners/summary')),
  filters: () => data<{ regions: string[]; distributors: { id: string; name: string; code: string; status: string }[] }>(api.get('/partners/filters')),
  get: (id: string) => data<PartnerDetail>(api.get(`/partners/${id}`)),
  create: (body: PartnerInput & { submit: boolean; openingBalance?: number; adminUser?: Record<string, string> | null }) =>
    data<PartnerDetail & { pendingApproval: PendingApprovalResult; adminUser: { id: string; email: string; name: string } | null }>(api.post('/partners', body)),
  update: (id: string, body: PartnerInput & { creditLimitReason?: string }) =>
    data<PartnerDetail & { pendingApproval: PendingApprovalResult; changedFields: string[] }>(api.put(`/partners/${id}`, body)),

  submit: (id: string) => data<PartnerDetail>(api.post(`/partners/${id}/submit`)),
  approve: (id: string, note?: string) => data<PartnerDetail>(api.post(`/partners/${id}/approve`, note ? { note } : {})),
  sendBack: (id: string, note: string) => data<PartnerDetail>(api.post(`/partners/${id}/send-back`, { note })),
  suspend: (id: string, body: { reason: string; blockRenewals: boolean }) => data<PartnerDetail>(api.post(`/partners/${id}/suspend`, body)),
  activate: (id: string) => data<PartnerDetail>(api.post(`/partners/${id}/activate`)),
  terminate: (id: string, reason: string) =>
    data<{ approvalId: string; subscriberCount: number }>(api.post(`/partners/${id}/terminate`, { reason })),
  approvals: (params: { partnerId?: string; status?: string }) => data<ApprovalRow[]>(api.get('/partners/approvals', { params: clean(params) })),
  decide: (approvalId: string, body: { decision: 'approve' | 'reject' | 'cancel'; note?: string }) =>
    data<{ status: string; kind: string }>(api.post(`/partners/approvals/${approvalId}/decide`, body)),

  overview: (id: string) => data<PartnerOverview>(api.get(`/partners/${id}/overview`)),
  subscribers: (id: string, params: Record<string, string | number | undefined>) =>
    data<{ items: PartnerSubscriberRow[]; total: number; page: number; pageSize: number; operators: { id: string; name: string }[] }>(
      api.get(`/partners/${id}/subscribers`, { params: clean(params) })),

  network: (id: string) => data<PartnerNetwork>(api.get(`/partners/${id}/network`)),
  partnerView: (id: string) => data<PartnerView>(api.get(`/partners/${id}/network/partner-view`)),
  available: (id: string) => data<{ routers: AvailableRouter[]; pops: { id: string; name: string }[] }>(api.get(`/partners/${id}/network/available`)),
  stray: (id: string) => data<{ subscribers: RouterSubscriber[] }>(api.get(`/partners/${id}/network/stray`)),
  assign: (id: string, body: { routers: { nasId: string; poolIds: string[]; realm: string | null }[]; effectiveFrom: string; reason: string }) =>
    data<PartnerNetwork>(api.post(`/partners/${id}/network/assignments`, body)),
  changeAssignment: (id: string, assignmentId: string, body: { poolIds: string[]; realm: string | null; reason: string }) =>
    data<PartnerNetwork>(api.put(`/partners/${id}/network/assignments/${assignmentId}`, body)),
  removeAssignment: (id: string, assignmentId: string, reason: string) =>
    data<PartnerNetwork>(api.post(`/partners/${id}/network/assignments/${assignmentId}/remove`, { reason })),
  migrationTask: (id: string, assignmentId: string, note: string) =>
    data<{ id: string; subscriberCount: number; router: string }>(api.post(`/partners/${id}/network/assignments/${assignmentId}/migration-task`, { note })),
  assignmentSubscribers: (id: string, assignmentId: string) =>
    data<{ router: string; subscribers: RouterSubscriber[] }>(api.get(`/partners/${id}/network/assignments/${assignmentId}/subscribers`)),
  pops: () => data<Pop[]>(api.get('/partners/network/pops')),
  createPop: (body: { name: string; code?: string; city?: string }) => data<Pop>(api.post('/partners/network/pops', body)),
  updatePop: (popId: string, body: Partial<{ name: string; code: string | null; city: string | null; isActive: boolean }>) =>
    data<Pop>(api.put(`/partners/network/pops/${popId}`, body)),
  routers: () => data<RouterRow[]>(api.get('/partners/network/routers')),
  placeRouter: (nasId: string, body: { popId?: string | null; deviceRole?: 'nas' | 'bng' }) =>
    data<RouterRow>(api.put(`/partners/network/routers/${nasId}`, body)),

  wallet: (id: string, params: { page?: number; limit?: number; type?: string }) =>
    data<{ summary: WalletSummary; transactions: { items: WalletTxn[]; total: number; page: number; limit: number } }>(
      api.get(`/partners/${id}/wallet`, { params: clean(params) })),
  topup: (id: string, body: { amount: number; mode: string; reference: string; note?: string; idempotencyKey: string }) =>
    data<{ balance: number; available: number; duplicate: boolean }>(api.post(`/partners/${id}/wallet/topup`, body)),
  adjust: (id: string, body: { amount: number; reason: string }) =>
    data<{ balance: number; available: number }>(api.post(`/partners/${id}/wallet/adjust`, body)),

  billing: (id: string) => data<PartnerBilling>(api.get(`/partners/${id}/billing`)),
  generateSettlement: (id: string, period: string) => data<Settlement>(api.post(`/partners/${id}/settlements/generate`, { period })),
  markSettlementPaid: (settlementId: string, paymentReference: string) =>
    data<Settlement>(api.post(`/partners/settlements/${settlementId}/pay`, { paymentReference })),

  communication: (id: string) => data<PartnerComms>(api.get(`/partners/${id}/communication`)),
  setPrefs: (id: string, body: Partial<NotificationPrefs>) => data<NotificationPrefs>(api.put(`/partners/${id}/notification-prefs`, body)),
  sendMessage: (id: string, body: { channel: 'whatsapp' | 'sms'; to: string; message: string }) =>
    data<{ status: string; message: string }>(api.post(`/partners/${id}/messages`, body)),

  users: (id: string) => data<{ users: PartnerUser[]; roles: PartnerRole[]; sessions: ImpersonationSessionRow[] }>(api.get(`/partners/${id}/users`)),
  addUser: (id: string, body: { firstName: string; lastName?: string; email: string; phone?: string; roleId: string; password: string }) =>
    data<PartnerUser>(api.post(`/partners/${id}/users`, body)),
  updateUser: (id: string, userId: string, body: Partial<{ roleId: string; isActive: boolean }>) =>
    data<PartnerUser>(api.put(`/partners/${id}/users/${userId}`, body)),
  impersonate: (id: string, body: { userId: string; reason: string }) =>
    data<{
      accessToken: string; sessionId: string; expiresAt: string;
      partnerUser: { id: string; name: string; role: string }; partner: { id: string; name: string; code: string };
    }>(api.post(`/partners/${id}/impersonate`, body)),

  integrations: (id: string) => data<PartnerIntegrations>(api.get(`/partners/${id}/integrations`)),
  createApiKey: (id: string, body: { name: string; scopes: string[]; expiresAt?: string | null }) =>
    data<{ apiKey: string }>(api.post(`/partners/${id}/api-keys`, body)),
  revokeApiKey: (id: string, keyId: string) => data<PartnerIntegrations>(api.post(`/partners/${id}/api-keys/${keyId}/revoke`)),
  addWebhook: (id: string, body: { name?: string; url: string; events: string[] }) =>
    data<{ signingSecret: string }>(api.post(`/partners/${id}/webhooks`, body)),
  disableWebhook: (id: string, webhookId: string) => data<PartnerIntegrations>(api.post(`/partners/${id}/webhooks/${webhookId}/disable`)),

  setCapabilities: (id: string, body: Partial<Capabilities>) => data<Capabilities>(api.put(`/partners/${id}/capabilities`, body)),
  plans: (id: string) => data<PartnerPlans>(api.get(`/partners/${id}/plans`)),
  setPlans: (id: string, planIds: string[]) => data<PartnerPlans>(api.put(`/partners/${id}/plans`, { planIds })),

  auditTrail: (id: string, params: Record<string, string | number | undefined>) =>
    data<{ total: number; entries: AuditEntry[]; actions: string[]; users: string[] }>(api.get(`/partners/${id}/audit-trail`, { params: clean(params) })),

  importTemplate: () => data<{ columns: ImportColumn[]; maxRows: number }>(api.get('/partners/import/template')),
  importPreview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return data<ImportPreview>(api.post('/partners/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } }));
  },
  importCommit: (rows: ImportPreview['ready']) =>
    data<{ created: number; partners: { id: string; code: string; name: string }[]; skipped: ImportPreview['problems']; pendingApprovals: number }>(
      api.post('/partners/import/commit', { rows: rows.map((r) => ({ rowNumber: r.rowNumber, raw: r.raw })) })),
};

export interface RouterSubscriber {
  id: string; subscriberCode: string; name: string; phone: string | null; status: string;
  expiryDate: string | null; plan: string | null; router: string | null;
}

export const portalApi = {
  overview: () => data<PortalOverview>(api.get('/partner-portal/overview')),
  network: () => data<PartnerView>(api.get('/partner-portal/network')),
  wallet: (params: { page?: number; limit?: number; type?: string }) =>
    data<{ summary: WalletSummary; transactions: { items: WalletTxn[]; total: number; page: number; limit: number } }>(
      api.get('/partner-portal/wallet', { params: clean(params) })),
  users: () => data<{ users: PartnerUser[]; roles: PartnerRole[] }>(api.get('/partner-portal/users')),
  addUser: (body: { firstName: string; lastName?: string; email: string; phone?: string; roleId: string; password: string }) =>
    data<PartnerUser>(api.post('/partner-portal/users', body)),
  updateUser: (userId: string, body: Partial<{ roleId: string; isActive: boolean }>) =>
    data<PartnerUser>(api.put(`/partner-portal/users/${userId}`, body)),
  endImpersonation: () => data<{ ended: boolean; partnerId: string | null }>(api.post('/partner-portal/impersonation/end')),
};

/* ── Helpers ────────────────────────────────────────────── */

function clean<T extends Record<string, unknown>>(params: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') out[k] = v;
  return out as Partial<T>;
}

/** The server's own explanation of what went wrong, or the fallback. */
export function apiError(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: Record<string, any> } })?.response?.data;
  const m = d?.error?.message ?? d?.message;
  if (Array.isArray(m)) return m.join('; ');
  if (typeof m === 'string' && m.trim()) return m;
  return fallback;
}

export function apiErrorCode(e: unknown): string | null {
  const d = (e as { response?: { data?: Record<string, any> } })?.response?.data;
  const c = d?.error?.code ?? d?.code;
  return typeof c === 'string' ? c : null;
}

/** ₹1,000 or ₹39.90. `paise` always gives two decimals (₹1,000.00), for columns of money that must line up. */
export function inr(n: number | null | undefined, opts: { signed?: boolean; paise?: boolean } = {}): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const digits = opts.paise || abs % 1 ? 2 : 0;
  const text = abs.toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits });
  const sign = n < 0 ? '−' : opts.signed && n > 0 ? '+' : '';
  return `${sign}₹${text}`;
}

export function int(n: number | null | undefined): string {
  return Number(n ?? 0).toLocaleString('en-IN');
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 60) return `${days} days ago`;
  return fmtDate(iso);
}

const IST = 'Asia/Kolkata';

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: IST });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: IST,
  });
}

export function initials(name: string): string {
  return name.split(/\s+/).filter((w) => /^[A-Za-z0-9]/.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
}

export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/** A browser download of rows as CSV (quoted, UTF-8 with BOM so Excel reads ₹ correctly). */
export function downloadCsv(filename: string, header: string[], rows: (string | number | null | undefined)[][]): void {
  const cell = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
  const blob = new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** What a second-approver request is for, in a few words. */
export function approvalTitle(a: { kind: ApprovalKind; payload: PendingApproval['payload'] }): string {
  switch (a.kind) {
    case 'termination':
      return 'Termination';
    case 'credit_limit':
      return `Credit limit ${inr(a.payload.from ?? 0)} → ${inr(a.payload.to ?? 0)}`;
    case 'commission_scheme':
      return `Commission version ${a.payload.version ?? ''} from ${fmtDate(a.payload.effectiveFrom)}`;
    default:
      return `${a.payload.partnerType ? TYPE_LABEL[a.payload.partnerType] : 'Partner-type'} commission default, version ${a.payload.version ?? ''}`;
  }
}

/** "Version 2 · Data 7% · Combined 9%", "Franchise default · Data 6%", or "No commission scheme". */
export function commissionSummary(c: PartnerDetail['commission'], type: PartnerType | null): string {
  if (c.source === 'none') return 'No commission scheme';
  const rates = [c.dataPct !== null && `Data ${c.dataPct}%`, c.combinedPct !== null && `Combined ${c.combinedPct}%`]
    .filter(Boolean)
    .join(' · ');
  const whose = c.source === 'partner' ? `Version ${c.version}` : `${type ? TYPE_LABEL[type] : 'Partner-type'} default`;
  return rates ? `${whose} · ${rates}` : whose;
}

/** A fresh idempotency key per attempt, so a double-clicked top-up is recorded once. */
export function idempotencyKey(prefix: string): string {
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${rand}`;
}
